<template>
  <div class="max-w-content-container mx-auto gap-y-4 flex flex-col">
    <div
      v-if="frequencyOptions.length"
      class="flex flex-col-reverse items-end justify-end lg:flex-row-reverse lg:items-center"
    >
      <CommonFormRadios
        v-model="frequency"
        :options="frequencyOptions"
        label-classes="text-sm"
        @input="onFilterChange"
      />
    </div>

    <div v-if="!reports.length" class="text-center text-xl">
      There are currently no reports published for this section, please check
      back later
    </div>

    <div
      v-if="
        data.latestReports.length &&
        props.showLatestReportSection &&
        currentPage === 1
      "
      data-testid="latest-reports"
      class="flex flex-col gap-y-4"
    >
      <div class="bg-s-200 px-3 py-4">
        <div class="flex justify-between items-center">
          <div class="text-xl font-bold">Latest Reports</div>
          <div class="font-semibold">Updated {{ getLatestReportDate() }}</div>
        </div>
      </div>

      <div class="grid gap-3 lg:grid-cols-2">
        <ReportCard
          v-for="report in data.latestReports"
          :key="report.ID"
          :report="report"
          :category="category"
          :initial-fields="initialFields"
          show-copy-link
        />
      </div>
    </div>

    <div v-if="data.years.length" class="gap-y-7 flex flex-col">
      <div class="flex flex-col gap-y-4">
        <div
          v-for="year in data.years"
          :key="year"
          class="flex flex-col gap-y-4"
        >
          <div class="bg-s-200 px-3 py-4 text-xl font-bold">
            {{ year }} Reports
          </div>
          <div class="grid gap-3 lg:grid-cols-2">
            <ReportCard
              v-for="report in data.groups[year]"
              :key="report.ID"
              :report="report"
              :category="category"
              :initial-fields="initialFields"
              show-copy-link
            />
          </div>
        </div>
      </div>

      <div class="flex justify-center lg:justify-start">
        <CommonPagination
          :page-size="pageSize"
          :total-records="currentReports.length"
          :current-page="currentPage"
          @page-change="updatePage"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/** packages */
import { format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'

/** components */
import CommonFormRadios from '~/components/common/FormRadios.vue'
import CommonPagination from '~/components/common/Pagination.vue'
import ReportCard from '~/components/shared/market/ReportCard.vue'

/** utils */
import { getFrequencies, getReportGroups } from '~/src/utils/reports/filter'
import {
  setReportGroupImages,
  setReportImages,
} from '~/src/utils/reports/image'

/** types */
import type { TReport, TReportCard } from '~/src/types/reports'

/** constants */
import { REPORT_ASSESSMENT_DATE } from '~/src/constants/reports'
import type { TRecord } from '~/src/types/common'
import type { TMarketSlug, TMarketType } from '~/src/types/markets'

const props = withDefaults(
  defineProps<{
    reports: TReport[]
    category?: TMarketType | undefined
    marketSlug: TMarketSlug
    showLatestReportSection?: boolean
    initialFields?: TRecord[]
  }>(),
  {
    showLatestReportSection: false,
    initialFields: () => [],
  },
)

const data = reactive({
  groups: {},
  years: [],
  latestReports: [],
})
const currentReports = ref([])
const frequency = ref(REPORT_ASSESSMENT_DATE.ALL)
const frequencyOptions = ref([])

const onPageChange = (page: number) => {
  const { years, groups, latestReports } = getReportGroups({
    currentPage: page,
    currentReports: currentReports.value,
    pageSize: pageSize.value,
    showLatestReportSection: props.showLatestReportSection,
  })

  if (frequency.value === REPORT_ASSESSMENT_DATE.ALL && page === 1) {
    setReportImages(latestReports, props.marketSlug)

    const startAt = props.showLatestReportSection ? latestReports.length : 0
    setReportGroupImages(groups, props.marketSlug, startAt)
  }

  data.years = years
  data.groups = groups
  data.latestReports = latestReports
}

const { pageSize, currentPage, updatePage, resetPagination } = usePagination({
  onPageChange,
})

const onFilterChange = debounce(() => {
  currentReports.value = props.reports.filter((report) => {
    if (frequency.value === REPORT_ASSESSMENT_DATE.ALL) return report
    return (
      frequency.value === REPORT_ASSESSMENT_DATE.ALL ||
      report.assessment_type === frequency.value
    )
  })
  resetPagination()
  onPageChange(currentPage.value)
}, 500)

const getLatestReportDate = () => {
  const latestReport: TReportCard = data.latestReports[0]
  return format(parseISO(latestReport.date), 'dd/MM/yy')
}

onMounted(() => {
  currentReports.value = [...props.reports].sort(
    (a, b) => parseISO(b.date).getTime() - parseISO(a.date).getTime(),
  )
  frequencyOptions.value = getFrequencies(currentReports.value)
  onPageChange(currentPage.value)
})
</script>
